import { useParams } from "react-router-dom";
import "../printStyles.css";
import { useAuth } from '../contexts/AuthContext';
import { incrementDocUsage } from '../utils/usage';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import React, { useState, useEffect } from "react";
import AccidentIncidentManagementPolicy from "../policies/AccidentIncidentManagementPolicy";
import AntiBriberyAndCorruptionPolicy from "../policies/AntiBriberyAndCorruptionPolicy";
import BreakdownAndRecoveryProcedure from "../policies/BreakdownAndRecoveryProcedure";
import CargoHandlingAndSecurityPolicy from "../policies/CargoHandlingAndSecurityPolicy";
import CommunicationAndReportingPolicy from "../policies/CommunicationAndReportingPolicy";
import ComplianceAndRegulatoryPolicy from "../policies/ComplianceAndRegulatoryPolicy";
import CustomerServicePolicy from "../policies/CustomerServicePolicy";
import DataProtectionPolicy from "../policies/DataProtectionPolicy";
import DriverPerformanceMonitoringPolicy from "../policies/DriverPerformanceMonitoringPolicy";
import DriverRecruitmentSelectionPolicy from "../policies/DriverRecruitmentSelectionPolicy";
import DrugAndAlcoholPolicy from "../policies/DrugAndAlcoholPolicy";
import EmergencyResponsePlan from "../policies/EmergencyResponsePlan";
import EmployeeGrievanceProcedure from "../policies/EmployeeGrievanceProcedure";
import EmployeePolicy from "../policies/EmployeePolicy";
import EnvironmentalPolicy from "../policies/EnvironmentalPolicy";
import EqualOpportunitiesDiversityPolicy from "../policies/EqualOpportunitiesDiversityPolicy";
import FleetManagementPolicy from "../policies/FleetManagementPolicy";
import FlexibleWorkingPolicy from "../policies/FlexibleWorkingPolicy";
import FuelManagementPolicy from "../policies/FuelManagementPolicy";
import HealthAndSafetyPolicy from "../policies/HealthAndSafetyPolicy";
import HealthAndWellbeingPolicy from "../policies/HealthAndWellbeingPolicy";
import InsuranceAndLiabilityPolicy from "../policies/InsuranceAndLiabilityPolicy";
import InvoicingPolicy from "../policies/InvoicingPolicy";
import ITAndCommunicationsPolicy from "../policies/ITAndCommunicationsPolicy";
import LoadSecuritySafeLoadingPolicy from "../policies/LoadSecuritySafeLoadingPolicy";
import MaternityPaternityPolicy from "../policies/MaternityPaternityPolicy";
import PregnancyPolicy from "../policies/PregnancyPolicy";
import QualityAssurancePolicy from "../policies/QualityAssurancePolicy";
import RedundancyPolicy from "../policies/RedundancyPolicy";
import ReturnToWorkPolicy from "../policies/ReturnToWorkPolicy";
import RoadworthinessMaintenancePolicy from "../policies/RoadworthinessMaintenancePolicy";
import RiskManagementPolicy from "../policies/RiskManagementPolicy";
import SocialMediaPolicy from "../policies/SocialMediaPolicy";
import SubcontractorManagementPolicy from "../policies/SubcontractorManagementPolicy";
import SustainabilityPolicy from "../policies/SustainabilityPolicy";
import TachographDriversHoursPolicy from "../policies/TachographDriversHoursPolicy";
import TemporaryAgencyWorkerPolicy from "../policies/TemporaryAgencyWorkerPolicy";
import TrainingAndDevelopmentPolicy from "../policies/TrainingAndDevelopmentPolicy";
import VehicleSecurityPolicy from "../policies/VehicleSecurityPolicy";
import VehicleMaintenancePolicy from "../policies/VehicleMaintenancePolicy";
import VORPolicy from "../policies/VORPolicy";
import WheelOffPolicy from "../policies/WheelOffPolicy";
import WhistleblowingPolicy from "../policies/WhistleblowingPolicy";
import WorkplaceBullyingHarassmentPolicy from "../policies/WorkplaceBullyingHarassmentPolicy";
import ZeroTolerancePolicy from "../policies/ZeroTolerancePolicy";
import { exportToWord } from '../utils/exportToWord';

// Today's date for printing
const today = new Date().toLocaleDateString('en-GB');

const PolicyViewer = () => {
  const { policyId } = useParams();
  const { user } = useAuth();
   const displayTitle = (policyId || 'policy')
     .replace(/-/g, ' ')
     .replace(/\b\w/g, (c) => c.toUpperCase());

   React.useEffect(() => {
     if (user?.uid && policyId) {
       incrementDocUsage(user.uid, policyId, displayTitle, 'policy');
     }
   }, [user?.uid, policyId]);

  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('999');
  const selectedClient =
    clients.find(c => String(c.id) === String(selectedClientId)) ||
    { id: '999', name: 'Unamed', companyNumber: '', address: '' };

  useEffect(() => {
    if (!user) {
      setClients([{ id: '999', name: 'Unamed', companyNumber: '', address: '' }]);
      setSelectedClientId('999');
      return;
    }
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        const data = snap.data() || {};
        const list = (data.clients || []).map((c, idx) => ({
          id: String(idx + 1),
          name: c.companyName || '',
          companyNumber: c.companyNumber || '',
          address: c.address || '',
        }));
        list.push({ id: '999', name: 'Unamed', companyNumber: '', address: '' });
        setClients(list);
        const exists = list.some(c => String(c.id) === String(selectedClientId));
        if (!exists) {
          setSelectedClientId(list[0]?.id || '999');
        }
      } catch (err) {
        setClients([{ id: '999', name: 'Unamed', companyNumber: '', address: '' }]);
        setSelectedClientId('999');
      }
    })();
  }, [user?.uid]);

  const handleClientSelection = (event) => {
    setSelectedClientId(event.target.value);
  };

  const handleExportToWord = () => {
    const policyContent = document.getElementById('policy-content');
    if (policyContent) {
      exportToWord(policyContent.innerHTML, `${policyId}_policy.docx`);
    } else {
      alert("Policy content not found for export.");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getPolicyContent = (policyId) => {
    switch (policyId) {
      case "accident-incident-management":
        return (
          <AccidentIncidentManagementPolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
          />
        );
      case "anti-bribery-corruption":
        return (
          <AntiBriberyAndCorruptionPolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
          />
        );
      case "breakdown-recovery":
        return (
          <BreakdownAndRecoveryProcedure
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
          />
        );
      case "cargo-handling-security":
        return (
          <CargoHandlingAndSecurityPolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            transportManagerContact="123-456-7890"
            securityServiceContact="098-765-4321"
          />
        );
      case "communication-reporting":
        return (
          <CommunicationAndReportingPolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            transportManagerContact="123-456-7890"
            reportingContact="098-765-4321"
          />
        );
      case "compliance-regulatory":
        return (
          <ComplianceAndRegulatoryPolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            complianceOfficerContact="123-456-7890"
            regulatoryBodyContact="098-765-4321"
          />
        );
      case "customer-service":
        return (
          <CustomerServicePolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            customerServiceContact="123-456-7890"
            complaintContact="098-765-4321"
          />
        );
      case "data-protection-gdpr":
        return (
          <DataProtectionPolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            dataProtectionOfficerContact="123-456-7890"
            icoContact="098-765-4321"
          />
        );
      case "driver-performance-monitoring":
        return (
          <DriverPerformanceMonitoringPolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            performanceMonitoringContact="123-456-7890"
          />
        );
      case "driver-recruitment-selection":
        return (
          <DriverRecruitmentSelectionPolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            recruitmentContact="123-456-7890"
          />
        );
      case "drug-alcohol":
        return (
          <DrugAndAlcoholPolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            drugTestingContact="123-456-7890"
            alcoholTestingContact="098-765-4321"
          />
        );
      case "emergency-response-plan":
        return (
          <EmergencyResponsePlan
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            emergencyContact="123-456-7890"
          />
        );
      case "employee-grievance-procedure":
        return (
          <EmployeeGrievanceProcedure
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            grievanceContact="123-456-7890"
          />
        );
      case "employee-policy":
        return (
          <EmployeePolicy
            clientName={selectedClient?.name}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            transportManagerContact="123-456-7890"
            securityServiceContact="098-765-4321"
          />
        );
      case "environmental":
        return (
          <EnvironmentalPolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            environmentalOfficerContact="123-456-7890"
          />
        );
      case "equal-opportunities":
        return (
          <EqualOpportunitiesDiversityPolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            diversityOfficerContact="123-456-7890"
          />
        );
      case "fleet-management":
        return (
          <FleetManagementPolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            fleetManagerContact="123-456-7890"
          />
        );
      case "flexible-working":
        return (
          <FlexibleWorkingPolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            hrContact="123-456-7890"
          />
        );
      case "fuel-management":
        return (
          <FuelManagementPolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            fuelManagerContact="123-456-7890"
          />
        );
      case "health-safety":
        return (
          <HealthAndSafetyPolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            safetyOfficerContact="123-456-7890"
          />
        );
      case "health-wellbeing":
        return (
          <HealthAndWellbeingPolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            wellbeingOfficerContact="123-456-7890"
          />
        );
      case "insurance-liability":
        return (
          <InsuranceAndLiabilityPolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            insuranceContact="123-456-7890"
          />
        );
      case "InvoicingPolicy":
        return (
          <InvoicingPolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            hrContact="123-456-7890"
            transportManagerContact="123-456-7890"
          />
        );
      case "it-communications":
        return (
          <ITAndCommunicationsPolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            itSupportContact="123-456-7890"
          />
        );
      case "load-security-safe-loading":
        return (
          <LoadSecuritySafeLoadingPolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            loadingSupervisorContact="123-456-7890"
          />
        );
      case "maternity-paternity":
        return (
          <MaternityPaternityPolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            hrContact="123-456-7890"
          />
        );
      case "PregnancyPolicy":
        return (
          <PregnancyPolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            hrContact="123-456-7890"
            transportManagerContact="123-456-7890"
          />
        );
      case "quality-assurance":
        return (
          <QualityAssurancePolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            qualityManagerContact="123-456-7890"
          />
        );
      case "RedundancyPolicy":
        return (
          <RedundancyPolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            hrContact="123-456-7890"
            transportManagerContact="123-456-7890"
          />
        );
      case "return-to-work":
        return (
          <ReturnToWorkPolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            hrContact="123-456-7890"
          />
        );
      case "roadworthiness-maintenance":
        return (
          <RoadworthinessMaintenancePolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            maintenanceContact="123-456-7890"
          />
        );
      case "risk-management":
        return (
          <RiskManagementPolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            riskManagerContact="123-456-7890"
          />
        );
      case "social-media":
        return (
          <SocialMediaPolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            socialMediaContact="123-456-7890"
          />
        );
      case "subcontractor-management":
        return (
          <SubcontractorManagementPolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            subcontractorContact="123-456-7890"
          />
        );
      case "sustainability":
        return (
          <SustainabilityPolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            sustainabilityOfficerContact="123-456-7890"
          />
        );
      case "tachograph-drivers-hours":
        return (
          <TachographDriversHoursPolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            tachographContact="123-456-7890"
          />
        );
      case "temporary-worker-agency-worker":
        return (
          <TemporaryAgencyWorkerPolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            agencyContact="123-456-7890"
          />
        );
      case "training-development":
        return (
          <TrainingAndDevelopmentPolicy
            clientName={selectedClient?.name}
            companyNumber={selectedClient?.companyNumber}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            trainingContact="123-456-7890"
          />
        );
      case "vehicle-security":
        return (
          <VehicleSecurityPolicy
            clientName={selectedClient?.name}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            transportManagerContact="123-456-7890"
            securityServiceContact="098-765-4321"
          />
        );
      case "maintenance":
        return (
          <VehicleMaintenancePolicy
            clientName={selectedClient?.name}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            transportManagerContact="123-456-7890"
            securityServiceContact="098-765-4321"
          />
        );
      case "vor-policy":
        return (
          <VORPolicy
            clientName={selectedClient?.name}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            transportManagerContact="123-456-7890"
            securityServiceContact="098-765-4321"
          />
        );
      case "wheel-off":
        return (
          <WheelOffPolicy
            clientName={selectedClient?.name}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            transportManagerContact="123-456-7890"
            securityServiceContact="098-765-4321"
          />
        );
      case "whistleblowing":
        return (
          <WhistleblowingPolicy
            clientName={selectedClient?.name}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            transportManagerContact="123-456-7890"
            securityServiceContact="098-765-4321"
          />
        );
      case "workplace-bullying-harassment":
        return (
          <WorkplaceBullyingHarassmentPolicy
            clientName={selectedClient?.name}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            transportManagerContact="123-456-7890"
            securityServiceContact="098-765-4321"
          />
        );
      case "zero-tolerance":
        return (
          <ZeroTolerancePolicy
            clientName={selectedClient?.name}
            clientAddress={selectedClient?.address}
            effectiveDate={today}
            transportManagerContact="123-456-7890"
            securityServiceContact="098-765-4321"
          />
        );
      default:
        return <div>Policy not found</div>;
    }
  };

    return (
    <div className="p-6 bg-white">
      {/* Print date always visible */}
      <div className="text-right text-sm text-gray-600 mb-2">
        Printed: {today}
      </div>
      {/* Non-printable controls and headings */}
      <div className="no-print">
        {/* Back to Policies Button */}
        <div className="flex justify-start mb-4">
          <button
            type="button"
            onClick={() => window.history.back()}
            className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            ← Back to Policies
          </button>
        </div>
        {/* Client Selection Dropdown */}
        <div className="mt-4">
          <label className="block text-sm font-medium">Select Client:</label>
          <select
            className="mt-2 p-2 border border-gray-300 rounded-lg"
            value={selectedClient?.id || ""}
            onChange={handleClientSelection}
          >
            {clients.map((client) => (
              <option
                key={client.id}
                value={client.id}
                style={client.id === '999' ? { backgroundColor: '#333', color: '#fff' } : {}}
              >
                {client.name}{client.address ? ` - ${client.address}` : ''}
              </option>
            ))}
          </select>
        </div>
        
        {/* Display selected client details */}
        {selectedClient && selectedClient.name && (
          <div className="mt-6">
            <h3 className="text-2xl font-bold mb-4">
              Policy: {policyId?.replace(/-/g, " ") || "Policy not found"}
            </h3>
            <h4 className="text-lg font-bold">Selected Client: {selectedClient.name}</h4>
            <p className="text-sm">Company Address: {selectedClient.address}</p>
          </div>
        )}

        {/* Print Button */}
        <div className="mt-4 text-center print-button">
          <button
            onClick={handlePrint}
            className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition"
          >
            Print Policy
          </button>
        </div>
      </div>

      {/* Export to Word Button (always visible) */}
      <div className="mt-4 text-center">
        <button
          onClick={handleExportToWord}
          className="bg-green-500 text-white px-6 py-2 rounded-lg hover:bg-green-600 transition"
        >
          Export to Word
        </button>
      </div>
      
      {/* Printable area */}
      <div className="printable-form" id="policy-content">
        <div className="mb-6">
          {getPolicyContent(policyId)}
        </div>
        {/* Signature sections */}
        {/* ... (your signature JSX goes here) */}
      </div>
    </div>
  );
};

export default PolicyViewer;